import { Injectable, computed, inject, signal } from '@angular/core';
import {
  Auth, browserLocalPersistence, browserSessionPersistence, onAuthStateChanged, sendPasswordResetEmail,
  setPersistence, signInWithEmailAndPassword, signOut, User,
} from '@angular/fire/auth';
import { ISignUpRequest, IUser, IUserUpdate } from '../models/user.model';
import { ApiService } from './api.service';
import { initials, personName, shortPersonName } from '../utils/format.util';

export interface IAuthResult { ok: boolean; message?: string; field?: 'email' | 'password'; }

/**
 * Sesión de la consola. Firebase Auth (email/contraseña) + perfil de USERS (GET /account).
 * profile = 'sudo' | 'user'.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {

  private auth = inject(Auth, { optional: true });
  private api = inject(ApiService);

  readonly user = signal<IUser | null>(null);
  readonly ready = signal(false);
  readonly isSudo = computed(() => this.user()?.profile === 'sudo');
  readonly fullName = computed(() => personName(this.user()));
  readonly shortName = computed(() => shortPersonName(this.user()));
  readonly initials = computed(() => initials(this.fullName()));

  private firebaseUser: User | null = null;
  private readyPromise: Promise<void>;

  constructor() {
    this.readyPromise = new Promise<void>(resolve => {
      if (!this.auth) { this.ready.set(true); resolve(); return; }
      let first = true;
      onAuthStateChanged(this.auth, async fbUser => {
        this.firebaseUser = fbUser;
        if (fbUser && !this.user()) await this.loadProfile();
        if (!fbUser) this.user.set(null);
        if (first) { first = false; this.ready.set(true); resolve(); }
      });
    });
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR whenReady                                */
  /* -------------------------------------------------------------------------- */
  // region whenReady
  whenReady(): Promise<void> { return this.readyPromise; }

  get isLoggedIn(): boolean { return !!this.user(); }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR loadProfile                              */
  /* -------------------------------------------------------------------------- */
  // region loadProfile
  async loadProfile(): Promise<IUser | null> {
    const res = await this.api.get<{ user: IUser }>('/account');
    if (res.ok && res.data?.user) {
      this.user.set({ ...res.data.user, profile: res.data.user.profile ?? 'user' });
      return this.user();
    }
    return null;
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR login                                 */
  /* -------------------------------------------------------------------------- */
  // region login
  async login(email: string, password: string, remember: boolean): Promise<IAuthResult> {
    if (!this.auth) return { ok: false, message: 'No pudimos iniciar sesión.' };
    try {
      await setPersistence(this.auth, remember ? browserLocalPersistence : browserSessionPersistence);
      const cred = await signInWithEmailAndPassword(this.auth, email.trim(), password);
      this.firebaseUser = cred.user;
      const profile = await this.loadProfile();
      if (!profile) {
        await signOut(this.auth);
        return { ok: false, message: 'Tu cuenta no tiene acceso a la consola.' };
      }
      return { ok: true };
    } catch (e: any) {
      return { ok: false, message: AuthService.firebaseMessage(e?.code) };
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR register                               */
  /* -------------------------------------------------------------------------- */
  // region register
  async register(data: ISignUpRequest): Promise<IAuthResult> {
    const res = await this.api.post<{ user: IUser }>('/auth/signup', data);
    if (!res.ok) {
      const dup = /registrad/i.test(res.message || '');
      return { ok: false, message: dup ? 'Este correo ya tiene cuenta.' : res.message, field: dup ? 'email' : undefined };
    }
    return this.login(data.email, data.password, true);
  }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR recoverPassword                           */
  /* -------------------------------------------------------------------------- */
  // region recoverPassword
  async recoverPassword(email: string): Promise<IAuthResult> {
    if (!this.auth) return { ok: false, message: 'No pudimos enviar el enlace.' };
    try {
      await sendPasswordResetEmail(this.auth, email.trim());
      return { ok: true };
    } catch (e: any) {
      if (e?.code === 'auth/user-not-found') return { ok: true }; // no revelar si existe
      return { ok: false, message: AuthService.firebaseMessage(e?.code) };
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR updateProfile                            */
  /* -------------------------------------------------------------------------- */
  // region updateProfile
  async updateProfile(data: IUserUpdate): Promise<IAuthResult> {
    const res = await this.api.put<{ user: IUser }>('/account', data);
    if (!res.ok) return { ok: false, message: res.message };
    const current = this.user();
    this.user.set({ ...(current as IUser), ...(res.data?.user ?? {}), ...data });
    return { ok: true };
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR logout                                */
  /* -------------------------------------------------------------------------- */
  // region logout
  async logout(): Promise<void> {
    if (this.auth) await signOut(this.auth);
    this.user.set(null);
  }

  static firebaseMessage(code?: string): string {
    switch (code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
      case 'auth/invalid-email':
        return 'Correo o contraseña incorrectos.';
      case 'auth/too-many-requests':
        return 'Demasiados intentos. Intenta más tarde.';
      case 'auth/network-request-failed':
        return 'Sin conexión. Revisa tu internet.';
      case 'auth/user-disabled':
        return 'Tu cuenta está desactivada.';
      default:
        return 'No pudimos iniciar sesión. Intenta de nuevo.';
    }
  }
}
